import { LanguageChangedEvent } from '../../models/events/languageChangedEvent';
import { LanguageLoadedEvent } from '../../models/events/languageLoadedEvent';
import { PendoInitializedEvent } from '../../models/events/pendoInitializedEvent';
import { TenantChangedEvent } from '../../models/events/tenantChangedEvent';
import { TenantLoadedEvent } from '../../models/events/tenantLoadedEvent';
import { ThemeChangedEvent } from '../../models/events/themeChangedEvent';
import { ThemeLoadedEvent } from '../../models/events/themeLoadedEvent';
import { ThemeSelectChangedEvent } from '../../models/events/themeSelectChangedEvent';
import Logger from '../logger/logger';

class GlobalEventEmitter {
    static emitPortalShellLoaded() {
        this.emitEvent('portalShellLoaded');
    }

    static emitAccessTokenChanged(config: { organizationId: string; accessToken: string }) {
        this.emitEvent('accessTokenChanged', undefined, config);
    }

    static emitTenantsNotAvailable(defaultEmitter?: () => void) {
        this.emitEvent('tenantsNotAvailable', defaultEmitter);
    }

    static emitTenantChanged(tenant: TenantChangedEvent, defaultEmitter?: () => void) {
        this.emitEvent('tenantChanged', defaultEmitter, tenant);
    }

    static emitTenantLoaded(tenant: TenantLoadedEvent, defaultEmitter?: () => void) {
        this.emitEvent('tenantLoaded', defaultEmitter, tenant);
    }

    static emitThemeChanged(theme: ThemeChangedEvent, defaultEmitter?: () => void) {
        this.emitEvent('themeChanged', defaultEmitter, theme);
    }

    static emitThemeSelectChanged(theme: ThemeSelectChangedEvent, defaultEmitter?: () => void) {
        this.emitEvent('themeSelectChanged', defaultEmitter, theme);
    }

    static emitThemeLoaded(theme: ThemeLoadedEvent, defaultEmitter?: () => void) {
        this.emitEvent('themeLoaded', defaultEmitter, theme);
    }

    static emitLanguageChanged(language: LanguageChangedEvent, defaultEmitter?: () => void) {
        this.emitEvent('languageChanged', defaultEmitter, language);
    }

    static emitLanguageLoaded(language: LanguageLoadedEvent, defaultEmitter?: () => void) {
        this.emitEvent('languageLoaded', defaultEmitter, language);
    }

    static emitPendoInitialized(pendo: PendoInitializedEvent, defaultEmitter?: () => void) {
        this.emitEvent('pendoInitialized', defaultEmitter, pendo);
    }

    static emitLogout(defaultEmitter?: () => void) {
        this.emitEvent('logout', defaultEmitter);
    }

    static emitSessionTimedOut() {
        this.emitEvent('sessionTimedOut');
    }

    private static emitEvent(eventName: string, defaultEmitter?: () => void, data?: any, options: Omit<CustomEventInit, 'details'> = {
        composed: true,
        cancelable: true,
        bubbles: true,
    }) {
        // If there is a default emitter, it is V2
        if (defaultEmitter) {
            defaultEmitter();
            Logger.info([ 'store', 'event', eventName ], `emitted: ${JSON.stringify(data)}`);
            return;
        }

        Logger.info('GlobalEventEmitter', `${eventName} triggered: ${JSON.stringify(data)}`);
        const event = new CustomEvent(eventName, data ? {
            ...options,
            detail: data,
        } : undefined);
        window.document.dispatchEvent(event);
    }
}

export default GlobalEventEmitter;
